/**
 * Phone Validation Configuration - SHARED
 * 
 * SOURCE OF TRUTH for phone validation rules.
 * Used by both frontend and backend.
 * 
 * To add a new country:
 * 1. Add entry to PHONE_CONFIGS below
 * 2. Country will automatically be supported in both frontend and backend
 * 3. No code changes needed in validation logic
 */

export interface CountryPhoneConfig {
  countryCode: string;
  name: string;
  dialingCode: string;
  pattern: string; // Stored as string, converted to RegExp at runtime
  mobilePattern?: string;
  example: string;
}

/**
 * Country-specific phone validation configurations
 * Pattern strings will be converted to RegExp at runtime
 */
export const PHONE_CONFIGS: Record<string, CountryPhoneConfig> = {
  ZW: {
    countryCode: 'ZW',
    name: 'Zimbabwe',
    dialingCode: '+263',
    // Zimbabwe: +263 followed by 9 digits (mobile: 71, 73, 74, 77, 78)
    pattern: '^(\\+263|0)(7[13478]|8[68]|9[0-9])\\d{7}$',
    mobilePattern: '^(\\+263|0)(71|73|74|77|78)\\d{7}$',
    example: '+263-71-123-4567 or +263-77-123-4567'
  },
  DE: {
    countryCode: 'DE',
    name: 'Germany',
    dialingCode: '+49',
    // Germany: +49 followed by mobile prefixes (160/161/151 etc.)
    pattern: '^(\\+49|0)(1[5-7]\\d)\\d{8}$',
    mobilePattern: '^(\\+49|0)(1[5-7]\\d)\\d{8}$',
    example: '+49-160-12345678'
  },
  MT: {
    countryCode: 'MT',
    name: 'Malta',
    dialingCode: '+356',
    // Malta: +356 followed by 8 digits (mobile: 9xxx, 7xxx, 2xxx)
    pattern: '^\\+356\\d{8}$',
    mobilePattern: '^\\+356[9772]\\d{7}$',
    example: '+356-9999-9999'
  }
};

/**
 * SMS-specific configuration
 */
export const SMS_CONFIG = {
  maxRecipientsPerMessage: 1,
  validationMode: 'strict' as const, // 'strict' or 'lenient'
  fallbackToE164: true,
  // E.164 fallback pattern
  e164Pattern: '^\\+[1-9]\\d{1,14}$'
};

/**
 * Get list of supported countries
 */
export function getSupportedCountries(): string[] {
  return Object.keys(PHONE_CONFIGS);
}

/**
 * Get country config by code
 */
export function getCountryConfig(countryCode: string): CountryPhoneConfig | null {
  return PHONE_CONFIGS[countryCode] || null;
}

